import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE from '../config';
import { Mail, Lock, AlertCircle, ArrowRight, CheckCircle, X, Eye, EyeOff } from 'lucide-react';

const CustomerLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ enrollmentId: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot Password State
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1 = Email, 2 = OTP + New Password
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotStatus, setForgotStatus] = useState({ type: "", message: "" });

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });



  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/customer/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok && data.token) {
        localStorage.setItem("customerToken", data.token);
        localStorage.setItem("customerName", data.user.name);
        localStorage.setItem("customerEmail", data.user.email);
        localStorage.setItem("customerPhone", data.user.phone || "");

        setTimeout(() => {
          navigate("/customer-dashboard");
        }, 500);
      } else {
        setError(data.message || "Invalid credentials");
      }
    } catch (err) {
      setError("Unable to connect to server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotStatus({ type: "", message: "" });
    setLoading(true);

    try {
      if (forgotStep === 1) {
        // STEP 1: REQUEST OTP
        const res = await fetch(`${API_BASE}/api/customer/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: forgotEmail }),
        });
        const data = await res.json();

        if (res.ok) {
          setForgotStatus({ type: "success", message: data.message });
          setForgotStep(2); // Move to next step
        } else {
          // Handle Rate Limit Error specifically
          setForgotStatus({ type: "error", message: data.message || "Failed to send OTP." });
        }
      } else {
        // STEP 2: VERIFY OTP & RESET
        const res = await fetch(`${API_BASE}/api/customer/reset-password-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: forgotEmail, otp: forgotOtp, newPassword }),
        });
        const data = await res.json();

        if (res.ok) {
          setForgotStatus({ type: "success", message: "Password reset successful! Please login." });
          // Close modal after delay
          setTimeout(() => {
            setShowForgotModal(false);
            setForgotStep(1);
            setForgotEmail("");
            setForgotOtp("");
            setNewPassword("");
          }, 3000);
        } else {
          setForgotStatus({ type: "error", message: data.message || "Failed to reset password." });
        }
      }
    } catch (err) {
      setForgotStatus({ type: "error", message: "Server error. Please try again later." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row font-sans bg-gray-50 relative">

      {/* ================= FORGOT PASSWORD MODAL (OTP) ================= */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative animate-scaleIn">
            <button
              onClick={() => {
                setShowForgotModal(false);
                setForgotStep(1);
                setForgotEmail("");
                setForgotOtp("");
                setNewPassword("");
                setForgotStatus({ type: "", message: "" });
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>

            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 text-exiser-primary">
                <Lock size={24} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {forgotStep === 1 ? "Reset Password" : "Verify OTP"}
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                {forgotStep === 1
                  ? "Enter your registered email to receive an OTP."
                  : "Enter the OTP sent to your email and set a new password."}
              </p>
            </div>

            {forgotStatus.message && (
              <div
                className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${forgotStatus.type === "success"
                  ? "bg-green-50 text-green-700"
                  : "bg-red-50 text-red-700"
                  }`}
              >
                {forgotStatus.type === "success" ? (
                  <CheckCircle size={16} />
                ) : (
                  <AlertCircle size={16} />
                )}
                {forgotStatus.message}
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-4">
              {forgotStep === 1 ? (
                /* STEP 1: EMAIL */
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">
                    Registered Email
                  </label>
                  <input
                    type="email"
                    placeholder="name@example.com"
                    className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-exiser-primary/20 focus:border-exiser-primary transition-all text-sm"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
              ) : (
                /* STEP 2: OTP + NEW PASSWORD */
                <>
                  <div>
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">
                      Enter OTP
                    </label>
                    <input
                      type="text"
                      placeholder="123456"
                      className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-exiser-primary/20 focus:border-exiser-primary transition-all text-sm tracking-widest text-center font-bold"
                      value={forgotOtp}
                      onChange={(e) => setForgotOtp(e.target.value)}
                      maxLength={6}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">
                      New Password
                    </label>
                    <input
                      type="password"
                      placeholder="Enter new password"
                      className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-exiser-primary/20 focus:border-exiser-primary transition-all text-sm"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-exiser-primary text-white font-bold rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-70"
              >
                {loading
                  ? "Processing..."
                  : forgotStep === 1
                    ? "Send OTP"
                    : "Reset Password"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ================= LEFT: HERO BRANDING ================= */}
      <div className="lg:w-1/2 w-full bg-exiser-primary relative overflow-hidden flex flex-col justify-center p-12 lg:p-20 text-white">
        {/* Abstract Background Shapes */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
          <div className="absolute top-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-purple-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-lg">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-xs font-bold uppercase tracking-wider mb-6 text-purple-100">
            <CheckCircle size={14} className="text-green-400" /> Member Access
          </div>

          <h1 className="text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
            Welcome <br /> <span className="text-purple-200">Back.</span>
          </h1>

          <p className="text-lg text-purple-100 mb-8 leading-relaxed font-light">
            Sign in using your unique Course ID / Session ID or Registered Email.
          </p>

          <div className="flex gap-4 text-sm font-medium text-purple-200">
            <div className="flex items-center gap-2">
              <div className="w-1 h-1 bg-white rounded-full"></div> Secure Login
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1 h-1 bg-white rounded-full"></div> 24/7 Access
            </div>
          </div>
        </div>
      </div>

      {/* ================= RIGHT: LOGIN FORM ================= */}
      <div className="lg:w-1/2 w-full flex items-center justify-center p-6 lg:p-12 relative">
        <div className="w-full max-w-md bg-white p-8 md:p-10 rounded-3xl shadow-2xl border border-gray-100">

          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900">Sign In</h2>
            <p className="text-gray-500 mt-2 text-sm">Enter your credentials to access your account</p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-3 text-sm border border-red-100 animate-fadeIn">
              <AlertCircle size={20} className="shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">Course ID / Session ID / Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  name="enrollmentId"
                  type="text"
                  placeholder="EX-XXXXXX or name@example.com"
                  className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:outline-none focus:ring-2 focus:ring-exiser-primary/20 focus:border-exiser-primary transition-all text-sm font-medium placeholder-gray-400"
                  value={formData.enrollmentId}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center ml-1">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Password</label>
                <button
                  type="button"
                  onClick={() => setShowForgotModal(true)}
                  className="text-xs font-bold text-exiser-primary hover:underline hover:text-purple-700 transition-colors"
                >
                  Forgot Password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="w-full pl-12 pr-12 py-3.5 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:outline-none focus:ring-2 focus:ring-exiser-primary/20 focus:border-exiser-primary transition-all text-sm font-medium"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-exiser-primary text-white font-bold rounded-xl shadow-lg shadow-purple-900/10 hover:shadow-xl hover:shadow-purple-900/20 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 uppercase tracking-wide disabled:opacity-70 disabled:cursor-not-allowed mt-4"
            >
              {loading ? (
                <>Processing...</>
              ) : (
                <>Sign In <ArrowRight size={18} /></>
              )}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-100 text-center">
            <p className="text-sm text-gray-500">
              Don't have an account yet? <br />
              <span className="text-gray-400 text-xs">Accounts are created automatically upon course purchase.</span>
            </p>
          </div>
        </div>

        {/* Footer copyright for mobile/desktop alignment */}
        <div className="absolute bottom-6 text-center w-full text-xs text-gray-400 hidden lg:block">
          &copy; {new Date().getFullYear()} Exiser Pvt Ltd. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;